/**
 * THE NUKE (17 Jul) — immediate tenant purge, skipping the 30-day grace.
 *
 * Only offered on a tenant ALREADY scheduled for deletion (the "Purges
 * DD/MM/YYYY" badge) — the two-step rule: Delete… first, then this. For
 * testing, and for "delete my data right now" callers. Typed company name
 * re-checked server-side; the delete is the same bulk-cascade the overnight
 * purge job runs.
 */
import React, { useState } from 'react';
import { AlertTriangle, Download, Loader2, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { superadminApi } from '../lib/api';
import { toast } from 'sonner';

export default function PurgeNowDialog({ open, onOpenChange, tenant, onSuccess }) {
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);

  if (!tenant) return null;
  const nameMatches = confirmName.trim().toLowerCase() === (tenant.company_name || '').trim().toLowerCase();

  const download = async () => {
    try {
      const r = await superadminApi.exportTenant(tenant.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(tenant.company_name || 'company').replace(/\s+/g, '-')}-full-backup.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export their data');
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await superadminApi.actionTenant(tenant.id, 'purge_now', { confirm_name: confirmName });
      toast.success(r.data?.message || 'Tenant permanently deleted');
      onOpenChange(false);
      setConfirmName('');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setConfirmName(''); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-red-500">
            Purge now: {tenant.company_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">This skips the 30-day grace. It happens NOW.</p>
              <p className="text-muted-foreground text-xs mt-1">
                No undo, no waiting period. Everything is permanently erased the
                moment you confirm — people, bookings, balances — and every
                email address in the company is freed. If in doubt, close this
                and let the 30-day clock run instead.
              </p>
            </div>
          </div>

          <Button type="button" variant="outline" onClick={download} className="w-full" data-testid="purge-now-export">
            <Download className="w-4 h-4 mr-2" />
            Last chance: download their full backup
          </Button>

          <div>
            <Label>Type the company name to confirm: <span className="font-semibold">{tenant.company_name}</span></Label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                   className="input-hive mt-1" placeholder={tenant.company_name}
                   data-testid="purge-now-confirm" />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={confirm} disabled={busy || !nameMatches}
                    data-testid="purge-now-go">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Purging…</>)
                : (<><Zap className="w-4 h-4 mr-2" />Purge permanently now</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
